import { createFileRoute } from "@tanstack/react-router";
import { EsRevisionSpokePage } from "@/components/site/EsRevisionSpokePage";
import { ES_REVISION_HOW_PERFORMED, ES_REVISION_SPOKE_BY_PAGE } from "@/lib/es-revision-spokes";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { imageRights, PHYSICIAN_REF } from "@/lib/medical-schema";
import { procedureImageObjects } from "@/lib/procedure-media";
import galleryOversizedEs from "@/assets/sleeve-to-bypass-first-view-es.png.asset.json";
import galleryDissected from "@/assets/sleeve-to-bypass-dissection.png.asset.json";
import galleryCompleted from "@/assets/sleeve-to-bypass-completed-es.png.asset.json";

// The page key and path are inlined so the full Spanish revision content
// model is referenced only from the component and the server-only schema
// block below, keeping it out of the shared client bundle.
const PAGE = "revisionsSleeve" as const;
const url = `${SITE}/es/cirugia-de-revision/manga-gastrica`;
const galleryImage = (src: string, caption: string, description: string, width: number, height: number) => {
  const absolute = `${SITE}${src}`;
  return {
    "@type": "ImageObject" as const,
    url: absolute,
    contentUrl: absolute,
    caption,
    name: description,
    description,
    width,
    height,
    inLanguage: "es-US",
    ...imageRights(url),
  };
};
const PAGE_IMAGES = [
  ...procedureImageObjects(["sleeveNormalRevision", "sleeveStretchedRevision"], "es", url),
  galleryImage(
    galleryOversizedEs.url,
    "Una manga gástrica convertida en bypass gástrico por el Dr. Matthew Fitzer en 2025. Fotografías publicadas con el permiso del paciente.",
    "Primera vista laparoscópica de una manga gástrica sobredimensionada antes de convertirla en bypass gástrico, identificada con una flecha azul.",
    1400,
    867,
  ),
  galleryImage(galleryDissected.url, "2. La manga excesivamente grande (delineada por una línea punteada) durante la disección.", "Vista laparoscópica de la manga excesivamente grande delineada por una línea azul punteada durante la disección para una conversión de manga a bypass.", 976, 544),
  galleryImage(galleryCompleted.url, "3. La bolsa de bypass terminada, con el tamaño correcto.", "Vista laparoscópica de la bolsa de bypass gástrico terminada y de tamaño correcto después de la conversión de manga a bypass.", 640, 400),
  ...procedureImageObjects(["sleeveUgiRefluxPreEs", "sleeveUgiRefluxPostEs"], "es", url),
];

export const Route = createFileRoute("/es/cirugia-de-revision_/manga-gastrica")({
  head: () => ({
    ...esHead(PAGE),
    scripts: import.meta.env.SSR
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  ...esWebPageSchema(PAGE),
                  "@type": "MedicalWebPage",
                  reviewedBy: PHYSICIAN_REF,
                  mainEntity: { "@id": `${url}#procedure` },
                   primaryImageOfPage: PAGE_IMAGES[0],
                   image: PAGE_IMAGES,
                },
                {
                  "@type": "MedicalProcedure",
                  "@id": `${url}#procedure`,
                  mainEntityOfPage: { "@id": `${url}#webpage` },
                  procedureType: "https://schema.org/SurgicalProcedure",
                  name: ES_REVISION_SPOKE_BY_PAGE.revisionsSleeve.procedureName,
                  alternateName: ES_REVISION_SPOKE_BY_PAGE.revisionsSleeve.procedureAlternateNames,
                  description: ES_REVISION_SPOKE_BY_PAGE.revisionsSleeve.procedureDescription,
                  url,
                  howPerformed: ES_REVISION_HOW_PERFORMED[PAGE],
                   image: PAGE_IMAGES,
                },
                {
                  "@type": "FAQPage",
                  "@id": `${url}#faq`,
                  inLanguage: "es-US",
                  mainEntity: ES_REVISION_SPOKE_BY_PAGE.revisionsSleeve.faqs.map((faq, i) => ({
                    "@type": "Question",
                    "@id": `${url}#faq-${i + 1}`,
                    name: faq.q,
                    acceptedAnswer: { "@type": "Answer", text: faq.a },
                  })),
                },
              ],
            }),
          },
        ]
      : [],
  }),
  component: EsSleeveRevision,
});

function EsSleeveRevision() {
  return <EsRevisionSpokePage spoke={ES_REVISION_SPOKE_BY_PAGE.revisionsSleeve} />;
}
